import React from 'react';
import { Alert, Card, Col, Row, Table, Tooltip, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import type {
  PmDayRow,
  PmMode,
  PmModeColumn,
  PmSection,
  PmStumpStep,
  ProductMetricsResponse,
} from '@/types';
import { DASH, dayLabel, fmtNum, fmtPct } from '../bq-insights/bqMath';

const { Text } = Typography;

const MODE_LABELS: Record<PmMode, string> = {
  ALL: 'Overall',
  QP: 'Quick Play',
  CM: 'Custom Match',
  BS: 'Blitz',
};

const SECTION_LABELS: Record<PmSection, string> = {
  versions: 'the version list',
  daily: 'DAU, engagement and the stump funnel',
  matches: 'match engagement',
  retention: 'D1 / D3 retention',
};

/** 2.345 → '2.35'; null → '—'. */
const fmtRate = (v: number | null): string => fmtNum(v, 2);

/** Minutes as '1,234 h' once past 10 hours, else minutes. */
function fmtDuration(minutes: number): string {
  return minutes >= 600 ? `${fmtNum(minutes / 60)} h` : `${fmtNum(minutes, 1)} min`;
}

interface TileProps {
  label: string;
  value: string;
  hint: string;
  sub?: string;
}

const Tile: React.FC<TileProps> = ({ label, value, hint, sub }) => (
  <Card size="small" data-tile={label}>
    <Tooltip title={hint}>
      <Text type="secondary">{label}</Text>
    </Tooltip>
    <div style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3 }}>{value}</div>
    {sub && (
      <Text type="secondary" style={{ fontSize: 12 }}>
        {sub}
      </Text>
    )}
  </Card>
);

const Tiles: React.FC<{ data: ProductMetricsResponse }> = ({ data }) => {
  const { overview: o, retention: r } = data;
  const perDay = o.days > 1 ? `average of ${o.days} days` : undefined;
  const tiles: TileProps[] = [
    {
      label: 'DAU',
      value: fmtNum(o.dau),
      hint: 'Players active on a day (any event but pushes, updates and uninstalls)',
      sub: perDay,
    },
    {
      label: 'First-time users (D0 DAU)',
      value: fmtNum(o.d0Dau),
      hint: 'Active players on their install day',
      sub: perDay,
    },
    {
      label: 'Total engagement time',
      value: fmtDuration(o.engagementMinutes),
      hint: 'Foreground time of every player over the window (GA4 user_engagement)',
    },
    {
      label: 'Sessions per day',
      value: fmtRate(o.sessionsPerDau),
      hint: 'Sessions per active player per day',
    },
    {
      label: 'Engagement time per session',
      value: o.minutesPerSession === null ? DASH : `${fmtNum(o.minutesPerSession, 1)} min`,
      hint: 'Engagement time divided by sessions',
      sub: o.minutesPerDau === null ? undefined : `${fmtNum(o.minutesPerDau, 1)} min per DAU`,
    },
    {
      label: 'D1 retention',
      value: fmtPct(r.d1),
      hint: 'Installs of the window active the next day; install days whose next day is not built yet are left out',
      sub: `of ${fmtNum(r.d1Installs)} installs`,
    },
    {
      label: 'D3 retention',
      value: fmtPct(r.d3),
      hint: 'Installs of the window active 3 days later; install days whose day 3 is not built yet are left out',
      sub: `of ${fmtNum(r.d3Installs)} installs`,
    },
  ];
  return (
    <Row gutter={[12, 12]} data-section="tiles" style={{ marginBottom: 16 }}>
      {tiles.map((t) => (
        <Col key={t.label} xs={12} sm={8} lg={6}>
          <Tile {...t} />
        </Col>
      ))}
    </Row>
  );
};

const stumpColumns: ColumnsType<PmStumpStep> = [
  { title: 'Step', dataIndex: 'label', key: 'label' },
  {
    title: 'Claimed',
    key: 'claimed',
    align: 'right',
    render: (_: unknown, s) => fmtNum(s.claimed),
  },
  {
    title: '% of DAU',
    key: 'claimedOfDau',
    align: 'right',
    render: (_: unknown, s) => fmtPct(s.claimedOfDau),
  },
  {
    title: '% of previous',
    key: 'claimedOfPrevious',
    align: 'right',
    render: (_: unknown, s) => fmtPct(s.claimedOfPrevious),
  },
  {
    title: (
      <Tooltip title="Players with 1 / 2 / 3+ match wins that day: the server's rule for completing Stump 1 / 2 / 3">
        Completed
      </Tooltip>
    ),
    key: 'completed',
    align: 'right',
    render: (_: unknown, s) => fmtNum(s.completed),
  },
  {
    title: '% of DAU',
    key: 'completedOfDau',
    align: 'right',
    render: (_: unknown, s) => fmtPct(s.completedOfDau),
  },
  {
    title: '% of previous',
    key: 'completedOfPrevious',
    align: 'right',
    render: (_: unknown, s) => fmtPct(s.completedOfPrevious),
  },
];

const StumpFunnel: React.FC<{ data: ProductMetricsResponse }> = ({ data }) => (
  <Card
    size="small"
    title="Stump Reward funnel: DAU >> Stump Reward 1 >> 2 >> 3"
    data-section="stumps"
    style={{ marginBottom: 16 }}
  >
    <Table<PmStumpStep>
      rowKey="key"
      size="small"
      pagination={false}
      dataSource={data.stumps.steps}
      columns={stumpColumns}
      onRow={(s) => ({ 'data-step': s.key }) as React.HTMLAttributes<HTMLElement>}
    />
    <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
      Counted per player per day. Claimed = the stump_reward event; a Stump 2 or 3 claim counts only
      after the stump before it was claimed that day, or with the wins for it, which leaves out the
      claims the game logs right after the tutorial. The daily login bails were claimed{' '}
      {fmtNum(data.stumps.loginClaimed)} times. On install day the tutorial marks Stumps 1-2 done
      without wins, so day-0 completions read lower than the server&apos;s.
    </Text>
  </Card>
);

interface MatchRow {
  key: string;
  label: React.ReactNode;
  note?: string;
  value(c: PmModeColumn): string;
}

/** "1,234 (1.52 per player)". */
const countAndRate = (count: number, rate: number | null) =>
  rate === null ? fmtNum(count) : `${fmtNum(count)} (${fmtRate(rate)} per player)`;

function matchRows(): MatchRow[] {
  const rows: MatchRow[] = [
    {
      key: 'started',
      label: 'Match Started',
      note: 'New matches only: a resumed match is not counted again',
      value: (c) => fmtNum(c.started),
    },
    {
      key: 'startedD0',
      label: 'Match Started on D0',
      note: 'By players on their install day; resumes not counted',
      value: (c) => fmtNum(c.startedD0),
    },
    { key: 'completed', label: 'Match Completed', value: (c) => fmtNum(c.completed) },
    { key: 'completedD0', label: 'Match Completed on D0', value: (c) => fmtNum(c.completedD0) },
    {
      key: 'startedPerDau',
      label: 'Match Started per DAU',
      value: (c) => fmtRate(c.startedPerDau),
    },
    {
      key: 'completedPerDau',
      label: 'Match Completed per DAU',
      value: (c) => fmtRate(c.completedPerDau),
    },
    {
      key: 'startToComplete',
      label: 'Match Start to Complete %',
      note: 'Completed / started in the window; a match started before the window and finished in it counts as completed',
      value: (c) => fmtPct(c.startToComplete),
    },
    {
      key: 'resumed',
      label: 'Match Resumed',
      note: 'Resumes, kept out of Match Started',
      value: (c) => fmtNum(c.resumed),
    },
    {
      key: 'oversPerPlayer',
      label: 'Overs Played per user',
      note: 'Balls of both innings / 6, per player who started or resumed a match of this mode that day',
      value: (c) => fmtNum(c.oversPerPlayer, 1),
    },
  ];
  for (let dayN = 0; dayN <= 7; dayN++) {
    rows.push({
      key: `d${dayN}`,
      label: `Match Played (Completed) on D${dayN}`,
      note:
        dayN === 0
          ? 'Matches completed on that day since install, and per player active on that day since install'
          : undefined,
      value: (c) => {
        const v = c.byDayN.find((d) => d.dayN === dayN);
        return v ? countAndRate(v.completed, v.completedPerDau) : DASH;
      },
    });
  }
  return rows;
}

const MatchTable: React.FC<{ data: ProductMetricsResponse }> = ({ data }) => {
  const modes: PmMode[] = ['ALL', 'QP', 'CM', 'BS'];
  const columns: ColumnsType<MatchRow> = [
    {
      title: '',
      key: 'label',
      render: (_: unknown, r) =>
        r.note ? (
          <Tooltip title={r.note}>
            <span style={{ borderBottom: '1px dotted currentColor' }}>{r.label}</span>
          </Tooltip>
        ) : (
          r.label
        ),
    },
    ...modes.map((mode): ColumnsType<MatchRow>[number] => ({
      title: MODE_LABELS[mode],
      key: mode,
      align: 'right',
      render: (_: unknown, r) => {
        const col = data.matches.find((c) => c.mode === mode);
        return col ? r.value(col) : DASH;
      },
    })),
  ];
  const blitz = data.matches.find((c) => c.mode === 'BS');
  return (
    <Card size="small" title="Match Engagement" data-section="matches" style={{ marginBottom: 16 }}>
      <Table<MatchRow>
        rowKey="key"
        size="small"
        pagination={false}
        dataSource={matchRows()}
        columns={columns}
        scroll={{ x: true }}
        onRow={(r) => ({ 'data-row': r.key }) as React.HTMLAttributes<HTMLElement>}
      />
      {blitz && blitz.started === 0 && blitz.resumed === 0 && (
        <Text type="secondary" style={{ display: 'block', marginTop: 8, fontSize: 12 }}>
          No Blitz match was logged in this window: the client tags Blitz matches as mode BS, and
          none has reached GA4 yet.
        </Text>
      )}
    </Card>
  );
};

const dayColumns: ColumnsType<PmDayRow> = [
  { title: 'Day', dataIndex: 'date', key: 'date', render: dayLabel },
  { title: 'DAU', key: 'dau', align: 'right', render: (_: unknown, r) => fmtNum(r.dau) },
  { title: 'D0 DAU', key: 'd0', align: 'right', render: (_: unknown, r) => fmtNum(r.d0Dau) },
  {
    title: 'Sessions / DAU',
    key: 'spd',
    align: 'right',
    render: (_: unknown, r) => fmtRate(r.sessionsPerDau),
  },
  {
    title: 'Minutes / DAU',
    key: 'mpd',
    align: 'right',
    render: (_: unknown, r) => fmtNum(r.minutesPerDau, 1),
  },
  {
    title: 'Matches started',
    key: 'st',
    align: 'right',
    render: (_: unknown, r) => fmtNum(r.started),
  },
  {
    title: 'Matches completed',
    key: 'co',
    align: 'right',
    render: (_: unknown, r) => fmtNum(r.completed),
  },
  { title: 'D1 of installs', key: 'd1', align: 'right', render: (_: unknown, r) => fmtPct(r.d1) },
];

const ByDay: React.FC<{ data: ProductMetricsResponse }> = ({ data }) => (
  <Card size="small" title="By day" data-section="by-day">
    <Table<PmDayRow>
      rowKey="date"
      size="small"
      pagination={false}
      dataSource={data.byDay}
      columns={dayColumns}
      scroll={{ x: true }}
    />
  </Card>
);

/** Everything below the filters, for one answer of the endpoint. */
export const ProductMetricsBoard: React.FC<{ data: ProductMetricsResponse }> = ({ data }) => {
  const failed = data.unavailable.filter((s) => s !== 'versions');
  return (
    <div>
      {failed.length > 0 && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={`Couldn't load ${failed.map((s) => SECTION_LABELS[s]).join(', ')}`}
          description="Those figures show as empty. It tries again in about 2 minutes."
        />
      )}
      {data.filter.platform === 'ios' && (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message="iOS is not in the GA4 BigQuery export yet, so iOS figures read 0."
        />
      )}
      <Tiles data={data} />
      <StumpFunnel data={data} />
      <MatchTable data={data} />
      <ByDay data={data} />
    </div>
  );
};
